import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { AccessTime } from "@material-ui/icons";
import { ThemeType } from "@theme";
import { TimereportDayFormStore } from "../TimereportDayForm/TimereportDayFormStore";
import { ResetIcon } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import moment from "moment";

type Props = {
  dayFormStores: TimereportDayFormStore[];
  canEdit: boolean;
};

type DayName = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

type QuickSchedule = {
  label: string;
  workHours: { from: string; to: string };
  breakMinutes?: number;
  days: DayName[] | `${DayName}-${DayName}` | "all";
};

const dayOrder: DayName[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const fallbackDays: DayName[] = ["mon", "tue", "wed", "thu", "fri"];
const dayNameToNumberMapping: Record<DayName, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6
};

const schedules: QuickSchedule[] = [
  {
    label: "Mån-fre, 08-17, 60 min rast",
    workHours: { from: "08:00", to: "17:00" },
    breakMinutes: 60,
    days: "mon-fri"
  },
  {
    label: "Mån-fre, 08-16.30, 30 min rast",
    workHours: { from: "08:00", to: "16:30" },
    breakMinutes: 30,
    days: "mon-fri"
  },
  {
    label: "Mån-fre, 08-16",
    workHours: { from: "08:00", to: "16:00" },
    days: "mon-fri"
  }
];

export const QuickTimeReportButtons: React.FC<Props> = ({ dayFormStores, canEdit }) => {
  const classes = useStyles();

  if (!canEdit || !dayFormStores) {
    return null;
  }

  const parseRangeString = (range: string): DayName[] => {
    if (range === "all") return dayOrder;

    const parts = range.split("-");
    if (parts.length !== 2) {
      // Single day or fallback
      return dayOrder.includes(range as DayName) ? [range as DayName] : fallbackDays;
    }

    const [startDay, endDay] = parts as [DayName, DayName];
    const startIndex = dayOrder.indexOf(startDay);
    const endIndex = dayOrder.indexOf(endDay);

    if (startIndex === -1 || endIndex === -1) {
      return fallbackDays;
    }

    // Same day = full week starting from that day
    if (startIndex === endIndex) {
      return [...dayOrder.slice(startIndex), ...dayOrder.slice(0, startIndex)];
    }

    // Normal or wrapping range
    return startIndex <= endIndex
      ? dayOrder.slice(startIndex, endIndex + 1)
      : [...dayOrder.slice(startIndex), ...dayOrder.slice(0, endIndex + 1)];
  };

  const parseDays = (days: DayName[] | string): DayName[] => {
    return Array.isArray(days) ? days : parseRangeString(days);
  };

  const applySchedule = (schedule: QuickSchedule) => {
    const dayNames = parseDays(schedule.days);  
    const targetDayNumbers = dayNames.map((dayName) => dayNameToNumberMapping[dayName]);

    let targetDays = dayFormStores.filter((store) => {
      // A day the other side of a month-break week owns is not this booking's
      // to fill — the consultant reads it from the booking that owns it.
      if (!store.isMonthBreakOwned) {
        return false;
      }

      const dayOfWeek = moment.utc(store.initialValues.date).day();
      return targetDayNumbers.includes(dayOfWeek);
    });

    // For multi-day ranges, keep within current week
    if (dayNames.length > 1) {
      const earliestDate = dayFormStores.reduce((earliest, store) => {
        const date = moment.utc(store.initialValues.date);
        return !earliest || date.isBefore(earliest) ? date : earliest;
      }, null as moment.Moment | null);

      if (!earliestDate) {
        return;
      }

      const weekStart = earliestDate.clone().startOf("isoWeek");
      const weekEnd = weekStart.clone().endOf("isoWeek");

      targetDays = targetDays.filter((store) => {
        const date = moment.utc(store.initialValues.date);
        return date.isBetween(weekStart, weekEnd, null, "[]");
      });
    }

    targetDays.forEach((store) => {
      store.form.setValue("workTimeRanges", [schedule.workHours]);
      store.form.setValue("lunch", schedule.breakMinutes || 0);
      store.toggleDayOpen(true);
      store.form.validate();
    });
  };

  const clearAllDays = () => {
    dayFormStores.forEach((store) => {
      store.form.reset(store.initialValues, true);
      store.toDefault();
    });
  };

  const hasAnyDirtyForm = dayFormStores.some((store) => store.form.dirty);

  return (
    <div className={classes.container}>
      {schedules.map((schedule, index) => (
        <Button
          key={index}
          variant="outlined"
          size="small"
          color="primary"
          className={classes.button}
          startIcon={<AccessTime />}
          onClick={() => applySchedule(schedule)}
        >
          {schedule.label}
        </Button>
      ))}
      {hasAnyDirtyForm && (
        <Button
          variant="outlined"
          size="small"
          className={classes.button}
          startIcon={<ResetIcon />}
          onClick={clearAllDays}
        >
          {locale.Cancel}
        </Button>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  container: {
    display: "flex",
    gap: theme.spacing(1)
  },
  button: {
    backgroundColor: theme.palette.background.paper,
    textTransform: "none"
  }
}));
